// ziehAutoScroll.ts — beim Ziehen einer Beziehungszeile nahe am oberen oder
// unteren Rand weiterscrollen (User-Befund DEV 02.10.2026).
//
// Natives HTML5-Ziehen scrollt in den meisten Browsern nur das Fenster und nur
// ganz am Rand — die Akte scrollt aber in einem eigenen Container unter einem
// festen Kopf (ContactDetails bzw. Fenster des WindowManagers). Wer eine Person
// vom Haushalt auf eine Firma weiter unten ziehen wollte, kam nicht hin.
//
// Ablauf: Beim `dragstart` einer KMVS-Zeile (MIME `application/x-kmvs-…`)
// merkt sich der Helfer den scrollenden Vorfahren der gezogenen Zeile (sonst
// das Dokument). Während `dragover` (Capture-Phase am Dokument — Ziele mit
// `stopPropagation` verdecken sie so nicht) läuft eine requestAnimationFrame-
// Schleife: Steht der Zeiger weniger als RAND px vom sichtbaren oberen/unteren
// Rand entfernt — oder schon darüber hinaus, etwa auf dem festen Kopf —, wird
// gescrollt, umso schneller, je näher am Rand. `dragend`, `drop` und das
// Verlassen des Fensters beenden sie.

/** Abstand zum Rand, ab dem gescrollt wird (px). */
import { alleDokumente, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";
export const RAND = 60;
/** Nach so vielen ms ohne neue Zeigerposition hört das Scrollen auf. */
export const VERALTET_MS = 250;
/** Höchstgeschwindigkeit (px je Frame). */
export const MAX_TEMPO = 22;

/**
 * Scroll-Tempo für eine Zeigerposition: negativ = nach oben, positiv = nach
 * unten, 0 = nicht scrollen. `oben`/`unten` sind die sichtbaren Ränder des
 * scrollenden Bereichs (Viewport-Koordinaten). Jenseits des Rands (Zeiger auf
 * dem festen Kopf oder unter dem Bereich) gilt die Höchstgeschwindigkeit.
 */
export function scrollTempo(y: number, oben: number, unten: number, rand = RAND, max = MAX_TEMPO): number {
  if (unten - oben <= 2 * rand) rand = Math.max(8, (unten - oben) / 4);
  if (y < oben + rand) {
    const naehe = Math.min(1, (oben + rand - y) / rand);
    return -Math.max(1, Math.round(naehe * max));
  }
  if (y > unten - rand) {
    const naehe = Math.min(1, (y - (unten - rand)) / rand);
    return Math.max(1, Math.round(naehe * max));
  }
  return 0;
}

const scrollbar = (el: Element): boolean => {
  const s = getComputedStyle(el);
  return /(auto|scroll|overlay)/.test(s.overflowY) && el.scrollHeight > el.clientHeight + 1;
};

/** Nächster vertikal scrollender Vorfahr — sonst das Dokument. */
export function scrollbarerVorfahr(el: Element | null): Element {
  for (let x = el?.parentElement ?? null; x; x = x.parentElement) {
    if (scrollbar(x)) return x;
  }
  return document.scrollingElement ?? document.documentElement;
}

const istKmvsZug = (dt: DataTransfer | null): boolean =>
  !!dt && Array.from(dt.types ?? []).some((t) => t.startsWith("application/x-kmvs-"));

let eingerichtet = 0;
let abbauen: (() => void) | null = null;

/**
 * Einrichten (zählt mit — mehrere Tabellen teilen EINEN Satz Listener).
 * Liefert die Aufräumfunktion.
 */
export function ziehAutoScrollEinrichten(): () => void {
  eingerichtet += 1;
  if (eingerichtet === 1 && typeof window !== "undefined") abbauen = einrichten();
  return () => {
    eingerichtet -= 1;
    if (eingerichtet === 0) {
      abbauen?.();
      abbauen = null;
    }
  };
}

function einrichten(): () => void {
  let quelle: Element | null = null;
  let behaelter: Element | null = null;
  let y: number | null = null;
  let frame: number | null = null;
  /** Zeitpunkt der letzten Zeigerposition — ohne frische Position kein Weiterscrollen. */
  let zuletzt = 0;

  const ende = () => {
    quelle = null;
    behaelter = null;
    y = null;
    if (frame != null) cancelAnimationFrame(frame);
    frame = null;
  };

  const schritt = () => {
    frame = null;
    if (!behaelter || y == null) return;
    // Browser melden `dragover` auch bei ruhendem Zeiger laufend (~50 ms). Bleibt
    // es aus, ist der Zeiger woanders — nicht mit einer alten Position weiterlaufen.
    if (performance.now() - zuletzt > VERALTET_MS) return;
    const dok = behaelter === document.scrollingElement || behaelter === document.documentElement;
    const r = dok ? { top: 0, bottom: aktivesFenster().innerHeight } : behaelter.getBoundingClientRect();
    const oben = Math.max(0, r.top);
    const unten = Math.min(aktivesFenster().innerHeight, r.bottom);
    const tempo = scrollTempo(y, oben, unten);
    if (!tempo) return;
    const vorher = behaelter.scrollTop;
    behaelter.scrollTop = vorher + tempo;
    // Am Anschlag nicht weiterlaufen; sonst im nächsten Frame weiter, auch
    // wenn kein neues dragover kommt (der Zeiger steht still am Rand).
    if (behaelter.scrollTop !== vorher) frame = requestAnimationFrame(schritt);
  };

  // In der Capture-Phase von `dragstart` hat die Zeile ihre Daten noch nicht
  // gesetzt — die Art ist erst ab `dragover` lesbar. Deshalb hier nur die
  // Quelle merken und den Bereich beim ersten passenden `dragover` bestimmen.
  const start = (e: DragEvent) => {
    ende();
    quelle = e.target as Element;
  };
  const ueber = (e: DragEvent) => {
    if (!behaelter) {
      if (!quelle || !istKmvsZug(e.dataTransfer)) return;
      behaelter = scrollbarerVorfahr(quelle);
    }
    y = e.clientY;
    zuletzt = performance.now();
    if (frame == null) frame = requestAnimationFrame(schritt);
  };
  const raus = (e: DragEvent) => {
    // Nur beim Verlassen des Fensters (kein neues Ziel).
    if (!e.relatedTarget) {
      if (frame != null) cancelAnimationFrame(frame);
      frame = null;
    }
  };

  alleDokumente.addEventListener("dragstart", start, true);
  alleDokumente.addEventListener("dragover", ueber, true);
  alleDokumente.addEventListener("dragenter", ueber, true);
  alleDokumente.addEventListener("dragleave", raus, true);
  alleDokumente.addEventListener("dragend", ende, true);
  alleDokumente.addEventListener("drop", ende, true);
  return () => {
    ende();
    alleDokumente.removeEventListener("dragstart", start, true);
    alleDokumente.removeEventListener("dragover", ueber, true);
    alleDokumente.removeEventListener("dragenter", ueber, true);
    alleDokumente.removeEventListener("dragleave", raus, true);
    alleDokumente.removeEventListener("dragend", ende, true);
    alleDokumente.removeEventListener("drop", ende, true);
  };
}
